import Box from 'assets/components/box/Box';
import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import DataMap from 'assets/components/dataMap/DataMap';
import FormatValue from 'assets/components/formatValue/FormatValue';
import Icon from 'assets/components/icon/Icon';
import Page from 'assets/components/page/Page';
import ThemePageError from 'assets/components/page/themes/Theme.Page.Error';
import ThemeTableSimple from 'assets/components/table/themes/Theme.Table.Simple';
import ThemeTagRounded from 'assets/components/tag/themes/Theme.Tag.Rounded';
import { getLocales } from 'assets/locales/Locale';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import Theme from 'assets/themes/Theme.Common';
import { defaultDateTimeFormat, localizeDate } from 'assets/utils/data/Date';
import StatementStatusCode from 'models/enums/StatementStatusCode';
import React, { useState } from 'react';
import { StatementAction, useStatementProvider } from '../Statement.Hooks';
import { isEmpty } from 'lodash';
import ThemeWrapperStack from 'assets/themes/wrappers/Theme.Wrapper.Stack';

export const StatementListStyles = {
  title: {
    fontSize: Theme.FontNestedSize.XL,
    width: '100%',
    fontWeight: 'bold',
    wordBreak: 'break-all',
  } as React.CSSProperties,
  info: {
    textTransform: 'uppercase',
    lineHeight: 1,
    fontWeight: 'bold',
    fontSize: Theme.FontNestedSize.S,
    color: Theme.Color.secondary,
  } as React.CSSProperties,
  subtitle: {
    fontSize: Theme.FontNestedSize.M,
    color: Theme.Color.neutralDark,
    width: '100%',
    textOverflow: 'ellipsis',
    whiteSpace: 'pre',
    overflow: 'hidden',
    display: 'flex',
    flexWrap: 'wrap',
  } as React.CSSProperties,
};
export default function StatementList() {
  const { lang } = getLocales();
  const [isLoadingSelectAll, setIsLoadingSelectAll] = useState<boolean>(undefined);
  const {
    listApi,
    pageRouter,
    payStatement,
    setSelectedStatements,
    selectedStatements,
    exportStatementToPdf,
    downloadCancellationPdf,
    getAllSelectedStatements,
  } = useStatementProvider();
  const { permissions } = useAuthStore();

  return !listApi.payload && !listApi.isExecuting && listApi.apiCounter > 0 ? (
    <Page
      class={ThemePageError}
      sidebar={<Icon class="fas-clipboard-list" />}
      header={lang.statements}
      render={lang.noInfo}
    />
  ) : (
    <table className={ThemeTableSimple}>
      <thead>
        <tr>
          <th>
            <Button
              class={{ button: ThemeButtonCircle }}
              media={
                selectedStatements?.length === listApi.payload?.count
                  ? 'fas-check-square'
                  : selectedStatements?.length === 0
                    ? 'far-square'
                    : 'fas-minus-square'
              }
              disabled={!listApi?.payload?.count || !permissions.CREATE_STATEMENTS_FROM_CONTRACT_LIST}
              onClick={async () => {
                setIsLoadingSelectAll(true);
                setSelectedStatements(await getAllSelectedStatements());
                setIsLoadingSelectAll(undefined);
              }}
              isLoading={isLoadingSelectAll}
              active={!isEmpty(selectedStatements) ? true : false}
              htmlElementProps={{ title: lang.selectAll }}
            />
          </th>
          <th>
            {lang.dealer}/{lang.contracts}
          </th>
          <th>#</th>
          <th>{lang.createdAt}</th>
          <th>{lang.representative}</th>
          <th>{lang.reconciledBy}</th>
          <th>{lang.amount}</th>
          <th>{lang.tax}</th>
          <th>{lang.totalAmount}</th>
          <th data-actions>{lang.actions}</th>
        </tr>
      </thead>
      <tbody>
        <DataMap
          data={listApi.payload?.data}
          render={({ data: statement }) => {
            const currentStatement = selectedStatements?.find((it) => it.id === statement.id);
            const reconciledByName = statement.reconciledBy?.fullName;
            const reconciledAtLocal = localizeDate(statement.reconciledAtUtc, defaultDateTimeFormat);
            return (
              <tr key={statement.id} style={{ borderTop: `2px solid ${Theme.Color.shadow}` }}>
                <td>
                  <Button
                    onClick={() => {
                      if (currentStatement)
                        setSelectedStatements((prevStatements) =>
                          prevStatements?.filter((it) => it.id !== statement.id)
                        );
                      else setSelectedStatements((prevStatements) => [...(prevStatements || []), statement]);
                    }}
                    disabled={
                      !(
                        permissions.CREATE_STATEMENT ||
                        permissions.EDIT_STATEMENT ||
                        permissions.MODIFY_STATEMENT_EMAIL
                      )
                    }
                    class={ThemeButtonCircle}
                    media={<Icon class={currentStatement ? 'fas-check-square' : 'far-square'} />}
                    htmlElementProps={{ title: currentStatement ? lang.unselect : lang.select }}
                  />
                </td>
                <td>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <div style={{ ...StatementListStyles.info }}> {statement.displayInfo.info}</div>
                    <div style={{ ...StatementListStyles.title }}>{statement.displayInfo.title}</div>
                    <div>
                      {statement.isPayed && (
                        <Box
                          class={ThemeTagRounded}
                          style={{ color: Theme.Color.success, backgroundColor: Theme.Color.successLight }}
                        >
                          <Icon class="fas-check" />
                          &nbsp; {lang.paid}
                        </Box>
                      )}
                      {statement?.isEmailSent && (
                        <Box
                          class={ThemeTagRounded}
                          style={{ color: Theme.Color.success, backgroundColor: Theme.Color.successLight }}
                        >
                          <Icon class="fas-check" />
                          &nbsp; {lang.emailSent}
                        </Box>
                      )}
                    </div>
                  </div>
                </td>
                <td>{statement.statementNumber}</td>
                <td>{localizeDate(statement?.createdAtLocal, defaultDateTimeFormat)}</td>
                <td>{statement.representative?.fullName || 'N/A'}</td>
                <td>
                  {reconciledByName || reconciledAtLocal ? (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span>{reconciledByName || 'N/A'}</span>
                      <span>{reconciledAtLocal || 'N/A'}</span>
                    </div>
                  ) : (
                    'N/A'
                  )}
                </td>
                <td>
                  <b>
                    <FormatValue
                      value={`${(statement.contracts.reduce((sum, obj) => sum + obj.paymentAmountCAD, 0) || 0).toFixed(
                        2
                      )}$`}
                    />
                  </b>
                </td>
                <td>
                  <b>
                    <FormatValue
                      value={`${(statement.contracts.reduce((sum, obj) => sum + obj.paymentTaxCAD, 0) || 0).toFixed(
                        2
                      )}$`}
                    />
                  </b>
                </td>
                <td>
                  <b>
                    <FormatValue value={`${(statement.totalAmountDueCAD || 0).toFixed(2)}$`} />
                  </b>
                </td>
                <td>
                  <Box class={ThemeWrapperStack}>
                    <Button
                      onClick={pageRouter.updateParams}
                      data={{
                        id: statement.id.toString(),
                        action: StatementAction.CHANGE_STATUS,
                      }}
                      disabled={
                        !permissions.UPDATE_STATEMENT_STATUS ||
                        statement.status === StatementStatusCode.CANCELED ||
                        statement.status === StatementStatusCode.PRELIMINARY ||
                        statement.status === StatementStatusCode.RECONCILED
                      }
                      active
                      class={ThemeButtonCircle}
                      media={<Icon class="fas-check-double" />}
                      htmlElementProps={{ title: lang.changeStatus }}
                    />
                    <Button
                      onClick={pageRouter.updateParams}
                      data={{
                        id: statement.id.toString(),
                        action: StatementAction.UPLOAD_DOCUMENT,
                        router: pageRouter,
                      }}
                      class={ThemeButtonCircle}
                      media={<Icon class="fas-upload" />}
                      htmlElementProps={{ title: lang.upload }}
                      disabled={!permissions.EDIT_STATEMENT}
                      active
                    />
                    <Button
                      onClick={pageRouter.updateParams}
                      data={{
                        id: statement.id.toString(),
                        action: StatementAction.PRINT,
                        router: pageRouter,
                      }}
                      class={ThemeButtonCircle}
                      media={<Icon class="fas-print" />}
                      htmlElementProps={{ title: lang.print }}
                      disabled={
                        !(permissions.CREATE_STATEMENT || permissions.EDIT_STATEMENT) ||
                        statement.status === StatementStatusCode.CANCELED ||
                        statement.status === StatementStatusCode.PRELIMINARY
                      }
                      active
                    />
                    <Button
                      onClick={() => {
                        if (
                          statement.status === StatementStatusCode.CANCELED ||
                          statement.status === StatementStatusCode.PRELIMINARY
                        ) {
                          if (statement?.cancellationPdf) {
                            downloadCancellationPdf(statement);
                          }
                        } else {
                          exportStatementToPdf(statement.id, statement.statementNumber);
                        }
                      }}
                      disabled={
                        (statement.status === StatementStatusCode.CANCELED ||
                          statement.status === StatementStatusCode.PRELIMINARY) &&
                        !(statement as any)?.cancellationPdf
                      }
                      active
                      class={ThemeButtonCircle}
                      media={<Icon class="fas-download" />}
                      htmlElementProps={{ title: lang.download }}
                    />
                    <Button
                      onClick={pageRouter.updateParams}
                      data={{
                        id: statement.id.toString(),
                        action: StatementAction.SEND_EMAIL,
                        router: pageRouter,
                      }}
                      class={ThemeButtonCircle}
                      media={<Icon class="fas-envelope" />}
                      htmlElementProps={{ title: lang.sendEmail }}
                      disabled={
                        !(
                          permissions?.CREATE_STATEMENT ||
                          permissions.EDIT_STATEMENT ||
                          permissions?.MODIFY_STATEMENT_EMAIL
                        )
                      }
                      active
                    />
                    <Button
                      onClick={() => payStatement(statement)}
                      disabled={
                        statement.isPayed ||
                        statement.status === StatementStatusCode.RECONCILED ||
                        statement.status === StatementStatusCode.CANCELED ||
                        statement.status === StatementStatusCode.PRELIMINARY
                      }
                      active
                      class={ThemeButtonCircle}
                      media={<Icon class="fas-receipt" />}
                      htmlElementProps={{ title: lang.pay }}
                    />
                    <Button
                      onClick={pageRouter.updateParams}
                      data={{
                        id: statement.id.toString(),
                        action: StatementAction.DELETE,
                      }}
                      disabled={!permissions.DELETE_STATEMENT || statement.status !== StatementStatusCode.OPEN}
                      active
                      class={ThemeButtonCircle}
                      media={<Icon class="fas-trash" />}
                      htmlElementProps={{ title: lang.delete }}
                    />
                  </Box>
                </td>
              </tr>
            );
          }}
        />
      </tbody>
    </table>
  );
}
